import { Schedule } from "@aura-frontend/omnibus-az-client";
import {
  Button,
  Card,
  cn,
  DropdownMenu,
  DropdownMenuProps,
  Icons,
  Skeleton,
  useConfirmStore,
} from "@win-reality/react-ui";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ROUTES } from "@/lib/routes";
import { getScheduleDetailsLink } from "@/utils/scheduleUtils";

import { UpcomingFieldCardEvent } from "../SingleComplex/UpcomingFieldCardEvent";
import { DeleteEventMenuItem } from "./DeleteEventMenuItem";
import { useDeleteEvent } from "./FieldSchedule/scheduleMutations.queries";
import { MoveEventDialog } from "./MoveEventDialog/MoveEventDialog";
import { Source } from "./Source";

type UpcomingEventItemProps = {
  event: Schedule;
  /** The field's live game, so a started event can link to it */
  currentGameUuid?: string | null;
  fetchNextPage?: () => void;
  isLoaderRow?: boolean;
  isFetchingNextPage?: boolean;
};
function UpcomingEventItem({
  event,
  currentGameUuid,
  fetchNextPage,
  isLoaderRow,
  isFetchingNextPage,
}: UpcomingEventItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;
  const isFetchingRef = useRef(isFetchingNextPage);
  isFetchingRef.current = isFetchingNextPage;

  useEffect(() => {
    if (!isLoaderRow || !ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          !isFetchingRef.current &&
          fetchNextPageRef.current
        ) {
          fetchNextPageRef.current();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isLoaderRow]);
  const [isMoveFieldDialogOpen, setIsMoveFieldDialogOpen] = useState(false);

  const navigate = useNavigate();
  const { complexName, fieldId } = useParams<{
    complexName: string;
    fieldId: string;
  }>();

  const confirm = useConfirmStore((state) => state.confirm);
  const { deleteEvent } = useDeleteEvent();

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Confirm Event Deletion",
      description: "Are you sure you want to remove this event?",
      confirmText: "Delete Event",
      confirmButtonVariant: "destructive",
    });

    if (!confirmed) return;
    useConfirmStore.setState({ isPending: true });
    try {
      await deleteEvent(event);
    } finally {
      useConfirmStore.setState({ isOpen: false, isPending: false });
    }
  };

  const detailsLink = getScheduleDetailsLink(event, currentGameUuid);
  const detailsItems: DropdownMenuProps["items"] = detailsLink
    ? [
        {
          label: detailsLink.label,
          onClick: (e) => {
            e.stopPropagation();
            e.preventDefault();
            const path = ROUTES.EDIT_EVENT.replace(
              ":complexName",
              complexName ?? "",
            )
              .replace(":fieldId", fieldId ?? "")
              .replace(":eventId", detailsLink.eventId);
            navigate(path);
          },
        },
      ]
    : [];

  const items: DropdownMenuProps["items"] = [
    ...detailsItems,
    {
      label: "Move Fields",
      onClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        setIsMoveFieldDialogOpen(true);
      },
    },

    <DeleteEventMenuItem
      key="delete-event"
      schedule={event}
      onDelete={handleDelete}
    />,
  ];

  if (isLoaderRow && isFetchingNextPage) {
    return <Skeleton className="rounded h-20 max-w-130 mx-auto" />;
  }

  return (
    <div>
      <Card ref={ref}>
        <div className="flex items-center justify-between">
          <UpcomingFieldCardEvent event={event} />

          <div className="flex items-center justify-between gap-1">
            <Source source={event.external?.source} />

            <DropdownMenu
              modal={false}
              variant="light"
              open={isOpen}
              onOpenChange={setIsOpen}
              align="end"
              items={items}
              onClick={(e) => e.stopPropagation()}
              trigger={
                <Button
                  className={cn(isOpen && "bg-[hsla(218,100%,52%,0.1)]")}
                  variant="ghost"
                  size="icon"
                >
                  <Icons.EllipsisVertical className="text-blue size-7!" />
                </Button>
              }
            />
          </div>
        </div>
      </Card>

      <MoveEventDialog
        event={event}
        open={isMoveFieldDialogOpen}
        onOpenChange={setIsMoveFieldDialogOpen}
      />
    </div>
  );
}

export { UpcomingEventItem };
